import { createServerFn } from '@tanstack/react-start'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'
import { sendTemplateEmail } from '@/lib/email-templates/send-email'
import { SIGNUP_BONUS_CREDITS } from '@/lib/creditPricing'

/**
 * One trigger, one recipient: the welcome email a new member expects right
 * after their first authenticated visit. Guarded by
 * profiles.welcome_email_sent_at so a member never receives it twice.
 */
export const sendWelcomeEmailIfNeeded = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId, claims } = context as {
      supabase: any
      userId: string
      claims: Record<string, unknown>
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('first_name, welcome_email_sent_at')
      .eq('id', userId)
      .maybeSingle()

    if (profileError) {
      console.error('[welcome-email] profile read failed', profileError.message)
      return { sent: false as const, reason: 'profile_unavailable' as const }
    }
    if (!profile || profile.welcome_email_sent_at) {
      return { sent: false as const, reason: 'already_sent' as const }
    }

    const email = typeof claims['email'] === 'string' ? (claims['email'] as string) : null
    if (!email) return { sent: false as const, reason: 'no_email' as const }

    // Claim first so a retry cannot double-send.
    const { error: claimError } = await supabase
      .from('profiles')
      .update({ welcome_email_sent_at: new Date().toISOString() })
      .eq('id', userId)
      .is('welcome_email_sent_at', null)
    if (claimError) {
      console.error('[welcome-email] claim failed', claimError.message)
      return { sent: false as const, reason: 'claim_failed' as const }
    }

    try {
      const result = await sendTemplateEmail('welcome', email, {
        templateData: {
          firstName: profile.first_name ?? undefined,
          credits: SIGNUP_BONUS_CREDITS,
        },
        idempotencyKey: `welcome-${userId}`,
      })
      if (!result.sent) {
        return { sent: false as const, reason: result.reason }
      }
      return { sent: true as const }
    } catch (error) {
      console.error(
        '[welcome-email] send failed',
        error instanceof Error ? error.message : String(error),
      )
      return { sent: false as const, reason: 'send_failed' as const }
    }
  })
